import React, { useCallback, useContext, useState } from 'react'
import { Grid, Button } from '@mui/material'
import { CognitoUser } from 'amazon-cognito-identity-js'
import ExternSurface from '../components/ExternSurface'
import {
    AnyInput,
    ConfirmPasswordValidator,
    FeedbackContext,
    InputValidator,
    useValidatedForm,
} from '@focaltec-suite/commons'
import { updatePassword } from '@focaltec-suite/api'
import { translate, cognitoErrorTranslator } from '@focaltec-suite/core'

interface UpdatePasswordFormProps {
    email: string
    cognitoUser?: CognitoUser
    onSuccess(): any
    onCancel(): any
}

interface SetupPasswordRequest {
    password: string
    confirm_password: string
}

function UpdatePasswordForm({
    email,
    cognitoUser,
    onSuccess,
    onCancel,
}: UpdatePasswordFormProps) {
    const feedbackContext = useContext(FeedbackContext)
    const { bind, register, isValid } = useValidatedForm<SetupPasswordRequest>()
    const [validators, setValidators] = useState<InputValidator[]>([])

    const hasChangedPassword = useCallback((name: string, value?: unknown) => {
        setValidators([
            new ConfirmPasswordValidator(() =>
                value ? (value as string) : ''
            ),
        ])
    }, [])

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!cognitoUser) {
            return
        }

        if (!isValid()) {
            return
        }

        feedbackContext.showBackdrop()

        const request = bind()
        updatePassword(cognitoUser, request.password)
            .then((response) => {
                switch (response.name) {
                    case 'success':
                        onSuccess()
                        break
                    default:
                        feedbackContext.showError(
                            `Unexpected status ${response.name}`
                        )
                }
            })
            .catch((error) =>
                cognitoErrorTranslator(error, feedbackContext.showError)
            )
            .finally(feedbackContext.closeBackdrop)
    }

    return (
        <ExternSurface title={translate('update.title') as string}>
            <form autoComplete="off" noValidate onSubmit={onSubmit}>
                <input type="hidden" id="email" name="email" value={email} />
                <Grid container justifyContent="center" alignItems="center">
                    <Grid item xs={12}>
                        <AnyInput
                            type="password"
                            name="password"
                            label={translate('password') as string}
                            required
                            register={register}
                            hiddenLabel
                            notifier={hasChangedPassword}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AnyInput
                            type="password"
                            name="confirm_password"
                            label={translate('confirm_password') as string}
                            validators={validators}
                            required
                            hiddenLabel
                            register={register}
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ my: 1 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            size="large"
                        >
                            {translate('update.button')}
                        </Button>
                    </Grid>
                    <Grid item xs="auto">
                        <Button
                            variant="text"
                            color="primary"
                            size="large"
                            onClick={onCancel}
                        >
                            {translate('cancel')}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </ExternSurface>
    )
}

export default UpdatePasswordForm
